import { useMemo } from "react";
import { FiPhone } from "react-icons/fi";
import { useIsAdmin } from "../../hooks/useIsAdmin";
import TransactionEditForm from "./TransactionEditForm";
import { useIsDarkTheme } from "../../hooks/useIsDarkTheme";

const fmtMoney = (n) =>
  new Intl.NumberFormat("sv-SE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(n || 0));

export default function TransactionDetailsPanel({
  isEditing,
  setIsEditing,
  transaction,
  status,
  sentAt,
  onDelete,
  onEditSubmit,
  busy = false,
  handleCopy = () => { },
}) {
  const isAdmin = useIsAdmin();
  const isPaid = status === "PAID";
  const isProcessing = status === "PROCESSING";
  const isDark = useIsDarkTheme();
  const locks = useMemo(
    () => ({
      countries: true,
      currencies: true,
      amounts: !isProcessing && !isAdmin,
      names: true,
      agent: isPaid && !isAdmin,
      phones: false,
    }),
    [isAdmin, isPaid, isProcessing]
  );

  const agentCountry = transaction?.recipientCountry || "";

  return (
    <>
      <div className="card border-0 shadow-sm rounded-3 mt-3 small">
        <div className="p-3 p-sm-4">
          <h5 className="">Transfer Details</h5>
          <hr className="" />

        </div>
        <div className="card-body p-3 p-sm-4">
          {!isEditing ? (
            <>
              <div className="row align-items-start g-3 pb-3 mb-3 border-bottom">
                <div className="col-12 col-md-8">
                  <div className="text-muted small">Destination Country</div>
                  <div className="fw-semibold d-flex align-items-center gap-2">
                    {transaction.recipientCountry && (
                      <img
                        src={`https://flagcdn.com/24x18/${transaction.recipientCountry.toLowerCase()}.png`}
                        alt={transaction.recipientCountry || "Country"}
                        className="rounded"
                        width={24}
                        height={18}
                        loading="lazy"
                      />
                    )}
                    {transaction.recipientCountry || "—"}
                  </div>
                </div>
                <div className="col-12 col-md-4 text-md-end">
                  <div className="text-muted small">Date</div>
                  <div className="fw-semibold">{sentAt}</div>
                </div>
              </div>

              <div className="row align-items-start g-3 pb-3 mb-3 border-bottom">
                <div className="col-12 col-md-8">
                  <div className="text-muted small">Sender</div>
                  <div className="fw-semibold">{transaction.senderName}</div>
                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                    {transaction.senderCountry && (
                      <img
                        src={`https://flagcdn.com/24x18/${transaction.senderCountry.toLowerCase()}.png`}
                        alt={transaction.senderCountry || "Country"}
                        className="rounded"
                        width={24}
                        height={18}
                        loading="lazy"
                      />
                    )}
                    <span className="text-muted">
                      {transaction.senderCountry || "—"}
                    </span>
                    {transaction.senderPhone && (
                      <>
                        <span className="text-muted">•</span>
                        <span className="d-inline-flex align-items-center gap-1">
                          <FiPhone />
                          <span className="text-muted">
                            {transaction.senderPhone}
                          </span>
                          <button
                            type="button"
                            className={`btn btn-sm py-0 px-2 ${isDark ? 'btn-outline-light' : 'btn-outline-secondary'}`}
                            title="Copy phone"
                            onClick={() => handleCopy(transaction.senderPhone)}
                          >
                            ⧉
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4 text-md-end">
                  <div className="text-muted small">Send Amount</div>
                  <div className="fw-semibold">
                    {fmtMoney(transaction.sendAmount)} {transaction.sendCurrency}
                  </div>
                </div>
              </div>

              <div className="row align-items-start g-3 pb-3 mb-3 border-bottom">
                <div className="col-12 col-md-8">
                  <div className="text-muted small">Recipient</div>
                  <div className="fw-semibold">{transaction.recipientName}</div>
                  <div className="d-flex align-items-center gap-2 mt-1 flex-wrap">
                    {transaction.recipientCountry && (
                      <img
                        src={`https://flagcdn.com/24x18/${transaction.recipientCountry.toLowerCase()}.png`}
                        alt={transaction.recipientCountry || "Country"}
                        className="rounded"
                        width={24}
                        height={18}
                        loading="lazy"
                      />
                    )}
                    <span className="text-muted">
                      {transaction.recipientCountry || "—"}
                    </span>
                    {transaction.recipientPhone && (
                      <>
                        <span className="text-muted">•</span>
                        <span className="d-inline-flex align-items-center gap-1">
                          <FiPhone />
                          <span className="text-muted">
                            {transaction.recipientPhone}
                          </span>
                          <button
                            type="button"
                            className={`btn btn-sm py-0 px-2 ${isDark ? 'btn-outline-light' : 'btn-outline-secondary'}`}
                            title="Copy phone"
                            onClick={() => handleCopy(transaction.recipientPhone)}
                          >
                            ⧉
                          </button>
                        </span>
                      </>
                    )}
                  </div>
                </div>
                <div className="col-12 col-md-4 text-md-end">
                  <div className="text-muted small">Receive Amount</div>
                  <div className="fw-semibold">
                    {fmtMoney(transaction.receiveAmount)}{" "}
                    {transaction.receiveCurrency}
                  </div>
                </div>
              </div>

              <div className="row align-items-start g-3 pb-2 mb-3 border-bottom">
                <div className="col-12 col-md-8">
                  <div className="text-muted small">Assigned Agent</div>
                  <div className="fw-semibold">
                    {transaction.agentName || "—"}
                  </div>
                </div>
                <div className="col-12 col-md-4 text-md-end">
                  <div className="text-muted small">Status</div>
                  <div className="fw-semibold">
                    <span className="badge text-bg-light border">{status}</span>
                  </div>
                </div>
              </div>

              <div className="row align-items-start g-3 pb-2">
                <div className="col-12 col-md-8">
                  <div className="text-muted small">Created By</div>
                  <div className="fw-semibold">
                    {transaction.creatorName || "—"}
                  </div>
                </div>
                <div className="col-12 col-md-4 text-md-end">
                  <div className="text-muted small">Role</div>
                  <div className="fw-semibold">
                    <span className="badge text-bg-light border">ADMIN</span>
                  </div>
                </div>
              </div>

              {isAdmin && (
                <div className="d-flex flex-wrap justify-content-end gap-2 mt-3">
                  {!isPaid && (
                    <button
                      className="btn btn-outline-primary rounded-3"
                      onClick={() => setIsEditing(true)}
                      disabled={busy}
                      type="button"
                    >
                      Edit Transfer
                    </button>
                  )}
                  <button
                    className="btn btn-outline-danger rounded-3"
                    onClick={onDelete}
                    disabled={busy}
                    type="button"
                  >
                    Delete
                  </button>
                </div>
              )}
            </>
          ) : (
            <TransactionEditForm
              transaction={transaction}
              status={status}
              sentAt={sentAt}
              locks={locks}
              busy={busy}
              agentCountry={agentCountry}
              onEditSubmit={onEditSubmit}
              setIsEditing={setIsEditing}
            />
          )}
        </div>
      </div>

    </>
  );
}
